import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, addDoc, getDocs, doc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { toast } from '../../components/ui/Toast';
import { useTranslation } from '../../i18n/LanguageContext';
import {
  IconArrowLeft,
  IconMail,
  IconCheck,
  IconCopy,
  IconUserPlus,
} from '@tabler/icons-react';

export default function StaffInvite() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { orgId, org, userProfile } = useAuth();
  const [sites, setSites] = useState([]);
  const [saving, setSaving] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [error, setError] = useState('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('operator');
  const [siteId, setSiteId] = useState('');

  useEffect(() => {
    const load = async () => {
      if (!orgId) return;
      try {
        const snap = await getDocs(collection(db, 'organisations', orgId, 'sites'));
        setSites(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error(err);
      }
    };
    load();
  }, [orgId]);

  const handleSend = async () => {
    if (!firstName.trim()) { setError(t('staff.err_first_name')); return; }
    if (!lastName.trim()) { setError(t('staff.err_last_name')); return; }
    if (!email.trim() || !email.includes('@')) { setError(t('staff.err_email')); return; }
    if (role !== 'org_admin' && !siteId) { setError(t('staff.err_site')); return; }

    setSaving(true);
    setError('');

    try {
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);

      const ref = await addDoc(collection(db, 'organisations', orgId, 'invites'), {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim().toLowerCase(),
        role,
        siteId: siteId || null,
        siteName: sites.find(s => s.id === siteId)?.name || null,
        orgId,
        orgName: org?.name || '',
        invitedById: userProfile?.id,
        invitedByName: userProfile?.name,
        accepted: false,
        expiresAt,
        createdAt: serverTimestamp(),
      });

      // Listen for the Cloud Function to populate tempPassword
      const unsubscribe = onSnapshot(
        doc(db, 'organisations', orgId, 'invites', ref.id),
        (snap) => {
          const d = snap.data();
          if (d?.tempPassword) {
            setCredentials({
              email: d.email,
              password: d.tempPassword,
              firstName: d.firstName,
            });
            setSaving(false);
            unsubscribe();
          } else if (d?.inviteError) {
            setError(t('staff.err_create_prefix') + ' ' + d.inviteError);
            setSaving(false);
            unsubscribe();
          }
        }
      );

      // Timeout safety
      setTimeout(() => {
        if (saving) {
          unsubscribe();
          setError(t('staff.err_timeout'));
          setSaving(false);
        }
      }, 30000);
    } catch (err) {
      console.error(err);
      setError(t('staff.err_create'));
      setSaving(false);
    }
  };

  const copyCredentials = () => {
    const text = t('staff.copy_greeting').replace('{name}', credentials.firstName) + '\n\n'
      + t('staff.copy_body') + '\n\n'
      + t('staff.copy_email') + ' ' + credentials.email + '\n'
      + t('staff.copy_temp_password') + ' ' + credentials.password + '\n\n'
      + t('staff.copy_footer');
    navigator.clipboard.writeText(text);
    toast.success(t('staff.copied_toast'));
  };

  const ROLES = [
    { value: 'org_admin', label: t('role.admin'),    description: t('staff.role_admin_desc')    },
    { value: 'manager',   label: t('role.manager'),  description: t('staff.role_manager_desc')  },
    { value: 'operator',  label: t('role.operator'), description: t('staff.role_operator_desc') },
  ];

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', maxWidth: 560, margin: '0 auto' }}>

        <div
          onClick={() => navigate('/staff')}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--color-text-secondary)', cursor: 'pointer', marginBottom: 20 }}
        >
          <IconArrowLeft size={15} stroke={1.5} /> {t('nav.staff')}
        </div>

        <h1 style={{ fontSize: 22, fontWeight: 400, marginBottom: 4 }}>{t('staff.add_member')}</h1>
        <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginBottom: 24 }}>
          {t('staff.add_member_sub')}
        </p>

        {credentials ? (
          <Card style={{ padding: '32px' }}>
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--color-success-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <IconCheck size={26} color="var(--color-success)" stroke={1.5} />
              </div>
              <div style={{ fontSize: 18, fontWeight: 400, marginBottom: 8 }}>{t('staff.account_created')}</div>
              <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)', lineHeight: 1.6 }}>
                {t('staff.share_details_pre')} <strong>{credentials.firstName}</strong>. {t('staff.share_details_post')}
              </div>
            </div>

            <div style={{ background: 'var(--color-bg-secondary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: 16, marginBottom: 20 }}>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', letterSpacing: '0.06em', marginBottom: 4 }}>{t('staff.label_email_caps')}</div>
                <div style={{ fontSize: 14, fontFamily: 'var(--font-mono)', color: 'var(--color-text-primary)' }}>{credentials.email}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', letterSpacing: '0.06em', marginBottom: 4 }}>{t('staff.label_temp_password_caps')}</div>
                <div style={{ fontSize: 14, fontFamily: 'var(--font-mono)', color: 'var(--color-text-primary)' }}>{credentials.password}</div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="secondary" fullWidth icon={<IconCopy size={14} stroke={1.5} />} onClick={copyCredentials}>
                {t('staff.copy_login_details')}
              </Button>
              <Button variant="primary" fullWidth onClick={() => {
                setCredentials(null);
                setFirstName(''); setLastName('');
                setEmail(''); setRole('operator'); setSiteId('');
              }}>
                {t('staff.add_another')}
              </Button>
            </div>
            <div style={{ marginTop: 12 }}>
              <Button variant="ghost" fullWidth onClick={() => navigate('/staff')}>
                {t('staff.back_to_staff')}
              </Button>
            </div>
          </Card>
        ) : (
          <>
            {error && (
              <div style={{ background: 'var(--color-danger-light)', border: '0.5px solid var(--color-danger)', borderRadius: 'var(--radius-md)', padding: '10px 14px', fontSize: 13, color: 'var(--color-danger)', marginBottom: 16 }}>
                {error}
              </div>
            )}

            <Card style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>{t('staff.personal_details')}</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                    {t('staff.first_name')} <span style={{ color: 'var(--color-danger)' }}>*</span>
                  </label>
                  <input type="text" placeholder={t('staff.first_name_placeholder')} value={firstName} onChange={e => setFirstName(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                    {t('staff.last_name')} <span style={{ color: 'var(--color-danger)' }}>*</span>
                  </label>
                  <input type="text" placeholder={t('staff.last_name_placeholder')} value={lastName} onChange={e => setLastName(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                  {t('staff.work_email')} <span style={{ color: 'var(--color-danger)' }}>*</span>
                </label>
                <input type="email" placeholder={t('staff.work_email_placeholder')} value={email} onChange={e => setEmail(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
              </div>
            </Card>

            <Card style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>{t('profile.role')}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {ROLES.map(r => (
                  <div
                    key={r.value}
                    onClick={() => setRole(r.value)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '12px 14px', borderRadius: 'var(--radius-md)',
                      border: '0.5px solid',
                      borderColor: role === r.value ? 'var(--color-brand)' : 'var(--color-border-light)',
                      background: role === r.value ? 'var(--color-brand-light)' : 'transparent',
                      cursor: 'pointer', transition: 'all 0.15s',
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 13, color: role === r.value ? 'var(--color-brand)' : 'var(--color-text-primary)', marginBottom: 2 }}>{r.label}</div>
                      <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>{r.description}</div>
                    </div>
                    {role === r.value && <IconCheck size={16} color="var(--color-brand)" stroke={2} />}
                  </div>
                ))}
              </div>
            </Card>

            {role !== 'org_admin' && (
              <Card style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>{t('staff.site_assignment')}</div>
                {sites.length === 0 ? (
                  <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
                    {t('staff.no_sites')}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {sites.map(site => (
                      <div
                        key={site.id}
                        onClick={() => setSiteId(site.id)}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '12px 14px', borderRadius: 'var(--radius-md)',
                          border: '0.5px solid',
                          borderColor: siteId === site.id ? 'var(--color-brand)' : 'var(--color-border-light)',
                          background: siteId === site.id ? 'var(--color-brand-light)' : 'transparent',
                          cursor: 'pointer', transition: 'all 0.15s',
                        }}
                      >
                        <div style={{ fontSize: 13, color: siteId === site.id ? 'var(--color-brand)' : 'var(--color-text-primary)' }}>
                          {site.name}
                        </div>
                        {siteId === site.id && <IconCheck size={16} color="var(--color-brand)" stroke={2} />}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <Button variant="secondary" onClick={() => navigate('/staff')}>{t('common.cancel')}</Button>
              <Button
                variant="primary"
                fullWidth
                onClick={handleSend}
                disabled={saving}
                loading={saving}
                icon={<IconUserPlus size={14} stroke={1.5} />}
              >
                {saving ? t('staff.creating_account') : t('staff.create_account')}
              </Button>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}